import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSilentSaveListener } from '../../../hooks/useSilentSaveListener';
import { useSafeSave } from '../../../hooks/useSafeSave';
import { motion, AnimatePresence } from 'framer-motion';
import { Save, Plus, Info, User, Calendar, Target, X, FileText, BarChart2, ChevronDown, Briefcase, Trash2 } from 'lucide-react';
import Button from '../../common/Button';
import { useTranslation } from 'react-i18next';
import WorkshopExportMenu from '../../common/WorkshopExportMenu';
import ParticipantsRACIMatrix from './ParticipantsRACIMatrix';
import RACIVisualization from './RACIVisualization';
import GuideModal from './GuideModal';
import { useBroadcast } from '../../../hooks/useBroadcast';
import useAnalysisStore from '../../../stores/useAnalysisStore';
import useAtelier1Store from '../../../stores/useAtelier1Store';
import { useAuth } from '../../../context/AuthContext';
import { POSITION_OPTIONS } from '../../../constants/index';
import { RiskAcceptanceTable, generateDefaultLevels } from '../../common/RiskLevelsDisplay';
import { GRAVITY_LEVELS } from '../../../constants/gravityLevels';
import { showSuccessToast, showErrorToast, showLoadingToast, updateToast } from '../../../utils/toastUtils';
import { createIdGenerator } from '../../../utils/idGenerator'; // Import ID generator

const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const Context = () => {
    const { t } = useTranslation();
    const atelier1Store = useAtelier1Store();
    const analysisStore = useAnalysisStore();
    const broadcastUpdate = useBroadcast();
    const isWorkshopDataLoading = analysisStore.isWorkshopDataLoading;
    const workshopDataError = analysisStore.workshopDataError;

    const currentContextData = atelier1Store.contextData;
    const currentAnalysis = analysisStore.currentAnalysis;

    const { user } = useAuth();
    const createdBy = user?.name || user?.email || 'Système EBIOS RM';

    // Safe save hook - prevents auto-save from overwriting data when not fully loaded
    const { markDataLoaded, markModified, canAutoSave, canExplicitSave, clearModified, useFormChangeDetector } = useSafeSave('Context');
    const formChangeRef = useFormChangeDetector();

    // Initialize ID generator for this analysis
    const idGen = currentAnalysis?.id ? createIdGenerator({ analysisId: currentAnalysis.id }) : null;

    // Local state for form fields
    const [organizationName, setOrganizationName] = useState('');
    const [missions, setMissions] = useState([]);
    const [newMission, setNewMission] = useState('');
    const [contraintes, setContraintes] = useState([]);
    const [newContrainte, setNewContrainte] = useState('');
    const [hypotheses, setHypotheses] = useState([]);
    const [newHypothese, setNewHypothese] = useState('');
    const [scope, setScope] = useState('');
    const [analysisDate, setAnalysisDate] = useState('');
    const [participants, setParticipants] = useState([]);
    const [matrix, setMatrix] = useState({});
    const [responsiblePersonsList, setResponsiblePersonsList] = useState([]);
    const [newResponsibleName, setNewResponsibleName] = useState('');
    const [newResponsibleRole, setNewResponsibleRole] = useState('');
    // Edit mode state for responsible persons
    const [editingResponsibleId, setEditingResponsibleId] = useState(null);
    const [editingResponsibleName, setEditingResponsibleName] = useState('');
    const [editingResponsibleRole, setEditingResponsibleRole] = useState('');
    const [isGuideOpen, setIsGuideOpen] = useState(false);
    const [isPdfMenuOpen, setIsPdfMenuOpen] = useState(false);
    const [isDocxMenuOpen, setIsDocxMenuOpen] = useState(false);

    // Default workshops
    const defaultWorkshops = [
        { id: 'Atelier 1', name: 'Atelier 1' },
        { id: 'Atelier 2', name: 'Atelier 2' },
        { id: 'Atelier 3', name: 'Atelier 3' },
        { id: 'Atelier 4', name: 'Atelier 4' },
        { id: 'Atelier 5', name: 'Atelier 5' }
    ];
    const [workshops, setWorkshops] = useState(defaultWorkshops);
    // Keep a ref so the data-load effect can read current workshops without making workshops a dependency
    const workshopsRef = useRef(defaultWorkshops);
    useEffect(() => { workshopsRef.current = workshops; }, [workshops]);

    // --- Effects ---
    useEffect(() => {
        // Use the ref here instead of `workshops` directly to avoid adding it as a dependency
        // (which caused a double-run: effect calls setWorkshops → workshops changes → effect runs again)
        const currentWorkshops = Array.isArray(workshopsRef.current) ? workshopsRef.current : [];

        if (currentContextData) {
            setOrganizationName(currentContextData.organizationName || '');
            setMissions(Array.isArray(currentContextData.missions) ? currentContextData.missions : (currentContextData.mission ? [currentContextData.mission] : []));
            setContraintes(Array.isArray(currentContextData.contraintes) ? currentContextData.contraintes : []);
            setHypotheses(Array.isArray(currentContextData.hypotheses) ? currentContextData.hypotheses : []);
            setScope(currentContextData.scope || '');
            setAnalysisDate(currentContextData.analysisDate || '');
            // Load responsible persons list
            setResponsiblePersonsList(Array.isArray(currentContextData.responsiblePersonsList) ? currentContextData.responsiblePersonsList : []);

            const contextParticipants = Array.isArray(currentContextData.participants) ? currentContextData.participants : [];

            if (contextParticipants.length > 0) {
                const participantsData = contextParticipants.map(p => ({
                    id: p.id,
                    name: p.name || '',
                    position: p.position || '',
                    customPosition: p.customPosition || '',
                    contact: p.contact || ''
                }));
                setParticipants(participantsData);

                const matrixData = {};
                const allWorkshopIdsInData = new Set();
                contextParticipants.forEach(p => {
                    if (p && p.workshopRoles && typeof p.workshopRoles === 'object') {
                        Object.keys(p.workshopRoles).forEach(wId => allWorkshopIdsInData.add(wId));
                    }
                });
                const defaultWorkshopIds = new Set(currentWorkshops.map(w => w.id));
                const combinedWorkshopIds = new Set([...defaultWorkshopIds, ...allWorkshopIdsInData]);

                const currentWorkshopMap = new Map(currentWorkshops.map(w => [w.id, w]));
                const workshopsToAdd = [];
                combinedWorkshopIds.forEach(id => {
                    if (!currentWorkshopMap.has(id)) {
                        workshopsToAdd.push({ id, name: id });
                    }
                });
                if (workshopsToAdd.length > 0) {
                    setWorkshops(prev => Array.isArray(prev) ? [...prev, ...workshopsToAdd] : workshopsToAdd);
                }

                if (currentContextData?.matrix) {
                    // Convert to per-workshop format
                    Object.entries(currentContextData.matrix).forEach(([id, role]) => {
                        if (role && typeof role === 'object' && !Array.isArray(role)) {
                            // Already per-workshop format: { "Atelier 1": ["R"], ... }
                            const normalized = {};
                            Object.keys(role).forEach(wId => {
                                normalized[wId] = Array.isArray(role[wId]) ? role[wId] : (role[wId] && role[wId] !== '' && role[wId] !== '-' ? [role[wId]] : []);
                            });
                            matrixData[id] = normalized;
                        } else {
                            // Old global array format: ["R", "A"] — spread to all workshops
                            const roles = Array.isArray(role) ? role : (role && role !== '' && role !== '-' ? [role] : []);
                            const perWorkshop = {};
                            currentWorkshops.forEach(w => { perWorkshop[w.id] = [...roles]; });
                            matrixData[id] = perWorkshop;
                        }
                    });
                } else {
                    contextParticipants.forEach(contextParticipant => {
                        if (contextParticipant?.workshopRoles && typeof contextParticipant.workshopRoles === 'object' && !Array.isArray(contextParticipant.workshopRoles)) {
                            // New per-workshop format
                            const normalized = {};
                            Object.keys(contextParticipant.workshopRoles).forEach(wId => {
                                const val = contextParticipant.workshopRoles[wId];
                                normalized[wId] = Array.isArray(val) ? val : (val && val !== '' && val !== '-' ? [val] : []);
                            });
                            matrixData[contextParticipant.id] = normalized;
                        } else if (contextParticipant?.role !== undefined) {
                            // Old global format — spread to all workshops
                            const role = contextParticipant.role;
                            const roles = Array.isArray(role) ? role : (role && role !== '' && role !== '-' ? [role] : []);
                            const perWorkshop = {};
                            currentWorkshops.forEach(w => { perWorkshop[w.id] = [...roles]; });
                            matrixData[contextParticipant.id] = perWorkshop;
                        } else {
                            // No roles — empty per-workshop
                            const perWorkshop = {};
                            currentWorkshops.forEach(w => { perWorkshop[w.id] = []; });
                            matrixData[contextParticipant.id] = perWorkshop;
                        }
                    });
                }
                setMatrix(matrixData);

            } else {
                // Create initial participant with new ID system
                const newId = idGen ? idGen.generateNext('participant', []) : `temp-p-${Date.now()}`;
                const defaultPosition = (POSITION_OPTIONS && POSITION_OPTIONS.length > 0) ? POSITION_OPTIONS[0].value : '';
                const initialParticipant = { id: newId, name: '', position: defaultPosition, customPosition: '', contact: '' };
                setParticipants([initialParticipant]);
                const emptyWorkshopRoles = {};
                currentWorkshops.forEach(w => { emptyWorkshopRoles[w.id] = []; });
                const initialMatrix = { [newId]: emptyWorkshopRoles };
                setMatrix(initialMatrix);
            }

            // ✅ Mark data as loaded - auto-save is now safe
            markDataLoaded();
        } else if (!currentContextData && !isWorkshopDataLoading && participants.length === 0) {
            // Create initial participant with new ID system
            const newId = idGen ? idGen.generateNext('participant', []) : `temp-p-${Date.now()}`;
            const defaultPosition = (POSITION_OPTIONS && POSITION_OPTIONS.length > 0) ? POSITION_OPTIONS[0].value : '';
            const initialParticipant = { id: newId, name: '', position: defaultPosition, customPosition: '', contact: '' };
            setParticipants([initialParticipant]);
            const emptyWorkshopRoles = {};
            currentWorkshops.forEach(w => { emptyWorkshopRoles[w.id] = []; });
            const initialMatrix = { [newId]: emptyWorkshopRoles };
            setMatrix(initialMatrix);
        }
    }, [currentContextData, isWorkshopDataLoading]); // ✅ removed `workshops` — using workshopsRef instead to prevent double-run

    // --- Handlers ---

    const handleSaveData = async () => {
        const currentParticipants = Array.isArray(participants) ? participants : [];
        const currentMatrix = typeof matrix === 'object' && matrix !== null ? matrix : {};
        const validParticipants = currentParticipants.filter(p => p && p.name && p.name.trim() !== '');

        if (validParticipants.length !== currentParticipants.length) {
            if (!window.confirm(t('context.errors.invalidParticipants'))) {
                return;
            }
        }

        const contextData = {
            organizationName,
            missions,
            contraintes,
            hypotheses,
            scope,
            analysisDate,
            responsiblePersonsList: responsiblePersonsList.filter(r => r && r.name && r.name.trim() !== ''),
            participants: validParticipants.map(p => {
                const participantData = {
                    id: p.id,
                    name: p.name,
                    position: p.position,
                    workshopRoles: currentMatrix[p.id] || {},
                    contact: p.contact || ''
                };
                if (p.position === "OTHER" && p.customPosition && p.customPosition.trim() !== '') {
                    participantData.customPosition = p.customPosition;
                }
                return participantData;
            })
        };

        const toastId = showLoadingToast(t('context.status.saving'));

        try {
            const response = await useAtelier1Store.getState().saveContextData(contextData, broadcastUpdate);
            if (response?.success) {
                updateToast(toastId, t('context.status.saved'), 'success');
                clearModified();
            } else {
                updateToast(toastId, `${t('context.errors.saveError')}: ${response?.message || 'Réponse invalide ou erreur inconnue'}`, 'error');
            }
        } catch (error) {
            updateToast(toastId, `${t('context.errors.saveError')}: ${t('context.errors.serverError')}`, 'error');
        }
    };

    // Silent save function for auto-save (no toast notifications)
    // GUARDED: Will only save if data was loaded AND user made modifications
    const handleSilentSave = async () => {
        // ⛔ Safety guard: don't save if data hasn't loaded or user hasn't changed anything
        if (!canAutoSave()) {
            return 'skipped'; // Signal to parent that no save was needed
        }

        const currentParticipants = Array.isArray(participants) ? participants : [];
        const currentMatrix = typeof matrix === 'object' && matrix !== null ? matrix : {};
        const validParticipants = currentParticipants.filter(p => p && p.name && p.name.trim() !== '');

        const contextData = {
            organizationName,
            missions,
            contraintes,
            hypotheses,
            scope,
            analysisDate,
            responsiblePersonsList: responsiblePersonsList.filter(r => r && r.name && r.name.trim() !== ''),
            participants: validParticipants.map(p => {
                const participantData = {
                    id: p.id,
                    name: p.name,
                    position: p.position,
                    workshopRoles: currentMatrix[p.id] || {},
                    contact: p.contact || ''
                };
                if (p.position === "OTHER" && p.customPosition && p.customPosition.trim() !== '') {
                    participantData.customPosition = p.customPosition;
                }
                return participantData;
            })
        };

        try {
            const response = await useAtelier1Store.getState().saveContextData(contextData, broadcastUpdate);
            if (response?.success) {
                clearModified(); // Reset modification flag after successful save
            }
            return response?.success || false;
        } catch (error) {
            console.error('Silent save error:', error);
            return false;
        }
    };

    useSilentSaveListener('context', handleSilentSave);


    const exportCurrentDataToPDF = async () => {
        try {
            const contextData = {
                organizationName,
                missions,
                contraintes,
                hypotheses,
                scope,
                analysisDate,
                participants: Array.isArray(participants) ? participants
                    .filter(p => p && p.name && p.name.trim() !== '')
                    .map(p => ({
                        id: p.id,
                        name: p.name,
                        position: p.position,
                        customPosition: p.customPosition || '',
                        contact: p.contact || '',
                        workshopRoles: matrix[p.id] || {}
                    })) : [],
                responsiblePersonsList: Array.isArray(responsiblePersonsList) ? responsiblePersonsList
                    .filter(r => r && r.name && r.name.trim() !== '')
                    .map(r => ({
                        name: r.name,
                        role: r.role || ''
                    })) : []
            };

            // Import PDF generator dynamically
            const { default: ContextPdfGenerator } = await import('../../../utils/contextPdfGenerator.js');

            // Create PDF generator instance with context data
            const pdfGenerator = new ContextPdfGenerator(contextData, {
                name: organizationName || 'Analyse EBIOS RM',
                companyName: organizationName || 'Organisation',
                createdBy: createdBy,
                riskLevelsCount: currentAnalysis?.riskLevelsCount || 4,
                riskLevels: currentAnalysis?.riskLevels || null,
                gravityLevels: currentAnalysis?.gravityLevels || null
            });

            // Generate PDF
            const pdfBlob = await pdfGenerator.generateContextReport();

            // Download PDF
            const url = URL.createObjectURL(pdfBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `EBIOS-RM_Contexte_${organizationName || 'Organisation'}_${new Date().toISOString().slice(0, 10)}_FR.pdf`;
            document.body.appendChild(a);
            a.click();

            // Cleanup
            setTimeout(() => {
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }, 0);

            showSuccessToast('Rapport PDF (FR) généré avec succès');
        } catch (error) {
            console.error('Error exporting PDF:', error);
            showErrorToast(`Erreur lors de l'export PDF: ${error.message}`);
        }
    };

    const exportCurrentDataToPDFEn = async () => {
        try {
            const contextData = {
                organizationName,
                missions,
                contraintes,
                hypotheses,
                scope,
                analysisDate,
                participants: Array.isArray(participants) ? participants
                    .filter(p => p && p.name && p.name.trim() !== '')
                    .map(p => ({
                        id: p.id,
                        name: p.name,
                        position: p.position,
                        customPosition: p.customPosition || '',
                        contact: p.contact || '',
                        workshopRoles: matrix[p.id] || {}
                    })) : [],
                responsiblePersonsList: Array.isArray(responsiblePersonsList) ? responsiblePersonsList
                    .filter(r => r && r.name && r.name.trim() !== '')
                    .map(r => ({
                        name: r.name,
                        role: r.role || ''
                    })) : []
            };

            // Import bilingual PDF generator dynamically (English)
            const { default: ContextPdfGenerator } = await import('../../../utils/contextPdfGenerator.js');

            // Create PDF generator instance with context data
            const pdfGenerator = new ContextPdfGenerator(contextData, {
                name: organizationName || 'EBIOS RM Analysis',
                companyName: organizationName || 'Organization',
                createdBy: createdBy,
                riskLevelsCount: currentAnalysis?.riskLevelsCount || 4,
                riskLevels: currentAnalysis?.riskLevels || null,
                gravityLevels: currentAnalysis?.gravityLevels || null
            }, 'en');

            // Generate PDF
            const pdfBlob = await pdfGenerator.generateContextReport();

            // Download PDF
            const url = URL.createObjectURL(pdfBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `EBIOS-RM_Context_${organizationName || 'Organization'}_${new Date().toISOString().slice(0, 10)}_EN.pdf`;
            document.body.appendChild(a);
            a.click();

            // Cleanup
            setTimeout(() => {
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }, 0);

            showSuccessToast('PDF Report (EN) generated successfully');
        } catch (error) {
            console.error('Error exporting PDF (EN):', error);
            showErrorToast(`Error exporting PDF: ${error.message}`);
        }
    };

    const exportCurrentDataToDocx = async () => {
        try {
            const contextData = {
                organizationName,
                missions,
                contraintes,
                hypotheses,
                scope,
                analysisDate,
                responsiblePersonsList: responsiblePersonsList.filter(r => r && r.name && r.name.trim() !== ''),
                participants: Array.isArray(participants) ? participants
                    .filter(p => p && p.name && p.name.trim() !== '')
                    .map(p => ({
                        id: p.id,
                        name: p.name,
                        position: p.position,
                        customPosition: p.customPosition || '',
                        contact: p.contact || '',
                        workshopRoles: matrix[p.id] || {}
                    })) : []
            };

            const { generateContextDocx } = await import('../../../utils/contextDocxGenerator.js');
            await generateContextDocx(contextData, {
                name: organizationName || 'Analyse EBIOS RM',
                companyName: organizationName || 'Organisation',
                createdBy: createdBy,
                riskLevelsCount: currentAnalysis?.riskLevelsCount || 4,
                riskLevels: currentAnalysis?.riskLevels || null,
                gravityLevels: currentAnalysis?.gravityLevels || null
            });

            showSuccessToast('Document Word (FR) généré avec succès');
        } catch (error) {
            console.error('Error exporting DOCX:', error);
            showErrorToast(`Erreur lors de l'export Word: ${error.message}`);
        }
    };

    const exportCurrentDataToDocxEn = async () => {
        try {
            const contextData = {
                organizationName,
                missions,
                contraintes,
                hypotheses,
                scope,
                analysisDate,
                responsiblePersonsList: responsiblePersonsList.filter(r => r && r.name && r.name.trim() !== ''),
                participants: Array.isArray(participants) ? participants
                    .filter(p => p && p.name && p.name.trim() !== '')
                    .map(p => ({
                        id: p.id,
                        name: p.name,
                        position: p.position,
                        customPosition: p.customPosition || '',
                        contact: p.contact || '',
                        workshopRoles: matrix[p.id] || {}
                    })) : []
            };

            const { generateContextDocx } = await import('../../../utils/contextDocxGenerator.js');
            await generateContextDocx(contextData, {
                name: organizationName || 'EBIOS RM Analysis',
                companyName: organizationName || 'Organization',
                createdBy: createdBy,
                riskLevelsCount: currentAnalysis?.riskLevelsCount || 4,
                riskLevels: currentAnalysis?.riskLevels || null,
                gravityLevels: currentAnalysis?.gravityLevels || null
            }, 'en');

            showSuccessToast('Word Report (EN) generated successfully');
        } catch (error) {
            console.error('Error exporting DOCX (EN):', error);
            showErrorToast(`Error generating English Word file: ${error.message}`);
        }
    };

    const handleAddMission = () => {
        if (newMission.trim() !== '') {
            setMissions(prev => [...prev, newMission.trim()]);
            setNewMission('');
            markModified();
        }
    };

    const handleRemoveMission = (index) => {
        setMissions(prev => prev.filter((_, i) => i !== index));
        markModified();
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddMission();
        }
    };

    const handleAddContrainte = () => {
        if (newContrainte.trim() !== '') {
            setContraintes(prev => [...prev, newContrainte.trim()]);
            setNewContrainte('');
            markModified();
        }
    };

    const handleRemoveContrainte = (index) => {
        setContraintes(prev => prev.filter((_, i) => i !== index));
        markModified();
    };

    const handleContrainteKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddContrainte();
        }
    };

    const handleAddHypothese = () => {
        if (newHypothese.trim() !== '') {
            setHypotheses(prev => [...prev, newHypothese.trim()]);
            setNewHypothese('');
            markModified();
        }
    };

    const handleRemoveHypothese = (index) => {
        setHypotheses(prev => prev.filter((_, i) => i !== index));
        markModified();
    };

    const handleHypotheseKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddHypothese();
        }
    };

    const handleAddParticipant = () => {
        // Generate new ID using ID generator
        const currentParticipants = Array.isArray(participants) ? participants : [];
        const newId = idGen ? idGen.generateNext('participant', currentParticipants) : `temp-p-${Date.now()}`;
        const defaultPosition = (Array.isArray(POSITION_OPTIONS) && POSITION_OPTIONS.length > 0) ? POSITION_OPTIONS[0].value : '';
        const newParticipant = { id: newId, name: "", position: defaultPosition, customPosition: "", contact: "" };

        setParticipants(prev => [...(Array.isArray(prev) ? prev : []), newParticipant]);

        setMatrix(prevMatrix => {
            const newMatrix = { ...(typeof prevMatrix === 'object' && prevMatrix !== null ? prevMatrix : {}) };
            const emptyWorkshopRoles = {};
            (Array.isArray(workshops) ? workshops : []).forEach(w => { emptyWorkshopRoles[w.id] = []; });
            newMatrix[newId] = emptyWorkshopRoles;
            return newMatrix;
        });

        markModified();
        showSuccessToast(t('context.status.participantAdded'));
    };

    // --- Responsible Persons Handlers ---
    const handleAddResponsiblePerson = () => {
        if (newResponsibleName.trim() !== '') {
            // Generate new ID using ID generator
            const newId = idGen ? idGen.generateNext('responsiblePerson', responsiblePersonsList) : `temp-rp-${Date.now()}`;
            const newResponsible = {
                id: newId,
                name: newResponsibleName.trim(),
                role: newResponsibleRole.trim() || ''
            };
            setResponsiblePersonsList(prev => [...prev, newResponsible]);
            setNewResponsibleName('');
            setNewResponsibleRole('');
            markModified();
            showSuccessToast(t('context.responsiblePersons.added'));
        }
    };

    const handleRemoveResponsiblePerson = (id) => {
        if (window.confirm(t('context.responsiblePersons.deleteConfirm'))) {
            setResponsiblePersonsList(prev => prev.filter(r => r.id !== id));
            markModified();
            showSuccessToast(t('context.responsiblePersons.removed'));
        }
    };

    const handleResponsibleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddResponsiblePerson();
        }
    };

    // --- Edit Responsible Person Handlers ---
    const handleStartEditResponsible = (responsible) => {
        setEditingResponsibleId(responsible.id);
        setEditingResponsibleName(responsible.name);
        setEditingResponsibleRole(responsible.role || '');
    };

    const handleSaveEditResponsible = () => {
        if (editingResponsibleName.trim() === '') {
            showErrorToast(t('context.responsiblePersons.nameRequired'));
            return;
        }
        
        setResponsiblePersonsList(prev => prev.map(r => 
            r.id === editingResponsibleId 
                ? { ...r, name: editingResponsibleName.trim(), role: editingResponsibleRole.trim() }
                : r
        ));
        
        setEditingResponsibleId(null);
        setEditingResponsibleName('');
        setEditingResponsibleRole('');
        markModified();
        showSuccessToast(t('context.responsiblePersons.updated'));
    };

    const handleCancelEditResponsible = () => {
        setEditingResponsibleId(null);
        setEditingResponsibleName('');
        setEditingResponsibleRole('');
    };

    const handleEditKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSaveEditResponsible();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            handleCancelEditResponsible();
        }
    };

    // --- Render Logic ---

    const riskLevelsConfig = useMemo(() => {
        const saved = currentAnalysis?.riskLevels;
        if (saved?.length) return saved;
        return generateDefaultLevels(currentAnalysis?.riskLevelsCount || 4);
    }, [currentAnalysis?.riskLevels, currentAnalysis?.riskLevelsCount]);

    const gravityLevelsConfig = useMemo(() => {
        const saved = currentAnalysis?.gravityLevels;
        const count = currentAnalysis?.riskLevelsCount || 4;
        if (saved?.length) return saved.slice(0, count);
        return GRAVITY_LEVELS.slice(0, count);
    }, [currentAnalysis?.gravityLevels, currentAnalysis?.riskLevelsCount]);

    if (isWorkshopDataLoading && !currentContextData) {
        return (
            <div className="flex justify-center items-center h-64 bg-slate-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-slate-600">{t('context.status.loading')}</p>
                </div>
            </div>
        );
    }

    // Show error specific to context loading
    if (workshopDataError?.context) {
        return (
            <div className="p-6 bg-slate-50 rounded-lg">
                <div className="bg-red-100 border border-red-300 text-red-800 p-4 rounded-lg mb-4 shadow-sm">
                    <h3 className="font-bold mb-2 text-lg flex items-center"><X size={20} className="mr-2" />{t('context.errors.loadingError')}</h3>
                    <p className="mb-3">{typeof workshopDataError.context === 'string' ? workshopDataError.context : JSON.stringify(workshopDataError.context)}</p>
                    <button
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-200 text-sm"
                        onClick={() => window.location.reload()} // Simple retry strategy
                    >
                        {t('context.errors.retry')}
                    </button>
                </div>
            </div>
        );
    }

    // Ensure defaults are arrays/objects if state is not yet initialized properly
    const displayParticipants = Array.isArray(participants) ? participants : [];
    const displayWorkshops = Array.isArray(workshops) ? workshops : [];
    const displayMatrix = typeof matrix === 'object' && matrix !== null ? matrix : {};
    const displayPositionOptions = Array.isArray(POSITION_OPTIONS) ? POSITION_OPTIONS : []; // Ensure it's an array

    return (
        <div ref={formChangeRef} className="space-y-8 p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">

            {/* === MERGED HEADER === */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 mb-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    {/* Left Side: Breadcrumb & Title */}
                    <div>
                        <div className="flex items-center text-sm text-slate-500 mb-2">
                            <span className="hover:text-blue-600 transition-colors">{t('context.breadcrumb.workshop1')}</span>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mx-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <span className="text-blue-600 font-medium">{t('context.breadcrumb.framing')}</span>
                        </div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center">
                            <Target size={28} className="mr-3 text-blue-600" />
                            {t('context.title')}
                        </h1>
                    </div>

                    {/* Right Side: Buttons */}
                    <div className="flex items-center gap-2 flex-wrap">

                        {/* Unified Export Menu */}
                        <WorkshopExportMenu
                            onExportPdfFr={exportCurrentDataToPDF}
                            onExportPdfEn={exportCurrentDataToPDFEn}
                            onExportDocxFr={exportCurrentDataToDocx}
                            onExportDocxEn={exportCurrentDataToDocxEn}
                        />

                        {/* Save Button */}
                        <Button
                            variant="primary"
                            color="blue"
                            icon={<Save />}
                            onClick={handleSaveData}
                            disabled={isWorkshopDataLoading}
                            title={t('context.tooltips.save')}
                        >
                            {t('context.buttons.save')}
                        </Button>

                        {/* Help Button */}
                        <Button
                            variant="primary"
                            color="blue"
                            icon={<Info />}
                            onClick={() => setIsGuideOpen(true)}
                        >
                            {t('context.buttons.help')}
                        </Button>
                    </div>
                </div>
            </div>
            {/* === END MERGED HEADER === */}

            {/* Guide Modal - positioned right under the header */}
            <AnimatePresence>
                <GuideModal isOpen={isGuideOpen} onClose={() => setIsGuideOpen(false)} />
            </AnimatePresence>

            {/* Main Content - Sections stacked vertically */}

            {/* Section 1: Basic Info */}
            <motion.div
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                className="space-y-6"
            >
                <div className="bg-white p-6 rounded-xl shadow-lg border border-slate-100 h-full overflow-hidden">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-semibold text-slate-800 flex items-center">
                            <Info size={20} className="mr-2 text-blue-600" />
                            {t('context.sections.generalInfo')}
                        </h2>
                        <div className="hidden sm:block px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                            {t('context.steps.step1')}
                        </div>
                    </div>
                    <div className="space-y-8">
                        {/* Organization Name and Date side-by-side with enhanced layout */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            {/* Organization Name */}
                            <div className="relative group transition-all duration-300 hover:shadow-md rounded-lg">
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                        <User size={18} className="text-blue-500 transition-colors duration-200" />
                                    </div>
                                    <input
                                        id="organizationName"
                                        type="text"
                                        className="w-full p-4 pl-11 border-2 border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 shadow-sm text-sm bg-white peer"
                                        value={organizationName}
                                        onChange={(e) => setOrganizationName(e.target.value)}
                                        required
                                        aria-label="Nom de l'organisation"
                                    />
                                    <label
                                        htmlFor="organizationName"
                                        className="absolute left-11 top-4 text-sm font-medium text-blue-600 transition-all duration-200 -translate-y-8 scale-75 transform origin-[0] bg-white px-1 peer-placeholder-shown:bg-transparent peer-placeholder-shown:text-slate-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8 peer-focus:bg-white peer-focus:text-blue-600 pointer-events-none"
                                    >
                                        {t('context.fields.organizationName')} <span className="text-red-500">{t('context.required')}</span>
                                    </label>
                                </div>
                                <p className="text-xs text-slate-500 mt-1.5 ml-2">
                                    {t('context.fields.organizationNameHelp')}
                                </p>
                            </div>

                            {/* Analysis Date with enhanced styling */}
                            <div className="relative group transition-all duration-300 hover:shadow-md rounded-lg">
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                        <Calendar size={18} className="text-blue-500 transition-colors duration-200" />
                                    </div>
                                    <input
                                        id="analysisDate"
                                        type="date"
                                        className="w-full p-4 pl-11 border-2 border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 shadow-sm text-sm bg-white peer"
                                        value={analysisDate}
                                        onChange={(e) => setAnalysisDate(e.target.value)}
                                        required
                                        aria-label="Date de l'analyse"
                                    />
                                    <label
                                        htmlFor="analysisDate"
                                        className="absolute left-11 top-4 text-sm font-medium text-blue-600 transition-all duration-200 -translate-y-8 scale-75 transform origin-[0] bg-white px-1 peer-placeholder-shown:bg-transparent peer-placeholder-shown:text-slate-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8 peer-focus:bg-white peer-focus:text-blue-600 pointer-events-none"
                                    >
                                        {t('context.fields.analysisDate')} <span className="text-red-500">{t('context.required')}</span>
                                    </label>
                                </div>
                                <p className="text-xs text-slate-500 mt-1.5 ml-2">
                                    {t('context.fields.analysisDateHelp')}
                                </p>
                            </div>
                        </div>


                        {/* Perimetre (Scope) and Mission side-by-side — first per user spec */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">


                            {/* Scope */}
                            <div className="relative group transition-all duration-300 hover:shadow-md rounded-lg w-full">
                                <div className="relative w-full">
                                    <div className="absolute top-4 left-0 flex items-start pl-3 pointer-events-none">
                                        <Target size={18} className="text-blue-500 transition-colors duration-200" />
                                    </div>
                                    <textarea
                                        id="scope"
                                        className="w-full p-4 pl-11 border-2 border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 shadow-sm text-sm bg-white peer min-h-[120px]"
                                        rows="5"
                                        value={scope}
                                        onChange={(e) => setScope(e.target.value)}
                                        required
                                        aria-label="Objectif ou Périmètre"
                                    />
                                    <label
                                        htmlFor="scope"
                                        className="absolute left-11 top-4 text-sm font-medium text-blue-600 transition-all duration-200 -translate-y-8 scale-75 transform origin-[0] bg-white px-1 peer-placeholder-shown:bg-transparent peer-placeholder-shown:text-slate-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-8 peer-focus:bg-white peer-focus:text-blue-600 pointer-events-none"
                                    >
                                        {t('context.fields.scope')} <span className="text-red-500">{t('context.required')}</span>
                                    </label>
                                </div>
                            </div>

                            {/* Mission List */}
                            <div className="relative group transition-all duration-300 hover:shadow-md rounded-lg">
                                <div className="relative border-2 border-slate-200 rounded-lg p-4 bg-white">
                                    <div className="flex items-center mb-3">
                                        <Target size={18} className="text-blue-500 mr-2" />
                                        <label className="text-sm font-medium text-blue-600">
                                            {t('context.fields.missions')}
                                        </label>
                                    </div>

                                    {/* Mission Input */}
                                    <div className="flex gap-2 mb-3">
                                        <input
                                            type="text"
                                            value={newMission}
                                            onChange={(e) => setNewMission(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            placeholder={t('context.fields.missionPlaceholder')}
                                            className="flex-1 p-2 border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleAddMission}
                                            className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors duration-200 flex items-center"
                                            title={t('context.buttons.addMission')}
                                        >
                                            <Plus size={16} />
                                        </button>
                                    </div>

                                    {/* Mission List */}
                                    <div className="space-y-2 min-h-[60px] max-h-32 overflow-y-auto">
                                        {missions.length === 0 ? (
                                            <p className="text-slate-400 text-sm italic">{t('context.table.noMissions')}</p>
                                        ) : (
                                            missions.map((mission, index) => (
                                                <div key={index} className="flex items-center justify-between bg-slate-50 p-2 rounded border">
                                                    <span className="text-sm flex-1">{mission}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveMission(index)}
                                                        className="ml-2 p-1 text-red-500 hover:bg-red-100 rounded transition-colors duration-200"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Hypotheses then Contraintes side-by-side */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6">


                            {/* Hypothèses List */}
                            <div className="relative group transition-all duration-300 hover:shadow-md rounded-lg">
                                <div className="relative border-2 border-slate-200 rounded-lg p-4 bg-white">
                                    <div className="flex items-center mb-3">
                                        <Target size={18} className="text-blue-500 mr-2" />
                                        <label className="text-sm font-medium text-blue-600">
                                            {t('context.fields.hypotheses') || 'Hypothèses'}
                                        </label>
                                    </div>

                                    {/* Hypothèse Input */}
                                    <div className="flex gap-2 mb-3">
                                        <input
                                            type="text"
                                            value={newHypothese}
                                            onChange={(e) => setNewHypothese(e.target.value)}
                                            onKeyPress={handleHypotheseKeyPress}
                                            placeholder={t('context.fields.hypothesePlaceholder') || 'Ajouter une hypothèse...'}
                                            className="flex-1 p-2 border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleAddHypothese}
                                            className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors duration-200 flex items-center"
                                            title={t('context.buttons.addMission')}
                                        >
                                            <Plus size={16} />
                                        </button>
                                    </div>

                                    {/* Hypothèse List */}
                                    <div className="space-y-2 min-h-[60px] max-h-32 overflow-y-auto">
                                        {hypotheses.length === 0 ? (
                                            <p className="text-slate-400 text-sm italic">{t('context.table.noMissions') || 'Aucune mission définie'}</p>
                                        ) : (
                                            hypotheses.map((item, index) => (
                                                <div key={index} className="flex items-center justify-between bg-slate-50 p-2 rounded border">
                                                    <span className="text-sm flex-1">{item}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveHypothese(index)}
                                                        className="ml-2 p-1 text-red-500 hover:bg-red-100 rounded transition-colors duration-200"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>
                            {/* Contraintes List */}
                            <div className="relative group transition-all duration-300 hover:shadow-md rounded-lg">
                                <div className="relative border-2 border-slate-200 rounded-lg p-4 bg-white">
                                    <div className="flex items-center mb-3">
                                        <Target size={18} className="text-blue-500 mr-2" />
                                        <label className="text-sm font-medium text-blue-600">
                                            {t('context.fields.contraintes') || 'Contraintes'}
                                        </label>
                                    </div>

                                    {/* Contrainte Input */}
                                    <div className="flex gap-2 mb-3">
                                        <input
                                            type="text"
                                            value={newContrainte}
                                            onChange={(e) => setNewContrainte(e.target.value)}
                                            onKeyPress={handleContrainteKeyPress}
                                            placeholder={t('context.fields.contraintePlaceholder') || 'Ajouter une contrainte...'}
                                            className="flex-1 p-2 border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleAddContrainte}
                                            className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors duration-200 flex items-center"
                                            title={t('context.buttons.addContrainte') || 'Ajouter'}
                                        >
                                            <Plus size={16} />
                                        </button>
                                    </div>

                                    {/* Contrainte List */}
                                    <div className="space-y-2 min-h-[60px] max-h-32 overflow-y-auto">
                                        {contraintes.length === 0 ? (
                                            <p className="text-slate-400 text-sm italic">{t('context.table.noContraintes') || 'Aucune contrainte définie'}</p>
                                        ) : (
                                            contraintes.map((item, index) => (
                                                <div key={index} className="flex items-center justify-between bg-slate-50 p-2 rounded border">
                                                    <span className="text-sm flex-1">{item}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveContrainte(index)}
                                                        className="ml-2 p-1 text-red-500 hover:bg-red-100 rounded transition-colors duration-200"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>


                        {/* Info section for both Mission and Scope */}
                        <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
                            <p className="text-sm text-blue-700 flex items-start">
                                <Info size={16} className="mr-2 flex-shrink-0 mt-0.5" />
                                <span>
                                    <strong>{t('context.advice.title')}</strong> {t('context.advice.missionScope')}
                                </span>
                            </p>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Section 2: RACI Matrix */}
            <motion.div
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                transition={{ delay: 0.1 }}
                className="space-y-6"
            >
                <div className="bg-white p-6 rounded-xl shadow-lg border border-slate-100">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
                        <h2 className="text-xl font-semibold text-slate-800 flex items-center">
                            <User size={20} className="mr-2 text-blue-600" />
                            {t('context.sections.participants')}
                            <span className="text-red-500 ml-1">{t('context.required')}</span>
                        </h2>
                        <div className="hidden sm:block px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                            {t('context.steps.step2')}
                        </div>
                        <div className="flex gap-2">
                            <Button
                                variant="primary"
                                color="blue"
                                icon={<Plus />}
                                onClick={handleAddParticipant}
                                title={t('context.buttons.addParticipant')}
                            >
                                {t('context.buttons.addParticipant')}
                            </Button>

                            <Button
                                variant="primary"
                                color="blue"
                                icon={<Save />}
                                onClick={handleSaveData}
                                disabled={isWorkshopDataLoading}
                                title={t('context.tooltips.save')}
                            >
                                {t('context.buttons.save')}
                            </Button>
                        </div>
                    </div>

                    {/* Participant Matrix Component */}
                    {displayParticipants.length > 0 ? (
                        <ParticipantsRACIMatrix
                            key="live-matrix"
                            workshops={displayWorkshops} // Already validated as array
                            positionOptions={displayPositionOptions} // Already validated as array
                            participants={displayParticipants} // Already validated as array
                            setParticipants={setParticipants}
                            matrix={displayMatrix} // Already validated as object
                            setMatrix={setMatrix}
                        />
                    ) : (
                        <div className="text-center py-8 px-4 border border-dashed border-slate-300 rounded-lg bg-slate-50">
                            <Info size={24} className='mx-auto text-slate-400 mb-2' />
                            <p className="text-slate-600">{t('context.table.noParticipants')}</p>
                            <p className="text-sm text-slate-500 mt-1">{t('context.table.addParticipantHint')}</p>
                        </div>
                    )}


                    {/* RACI Legend */}
                    <div className="mt-6 border-t border-slate-200 pt-6">
                        <h3 className="text-sm font-semibold text-slate-600 mb-3 uppercase tracking-wider flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            {t('context.raci.legend')}
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                            {[
                                { role: 'R', labelKey: 'responsible', descKey: 'responsibleDesc', color: 'green', bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' },
                                { role: 'A', labelKey: 'accountable', descKey: 'accountableDesc', color: 'blue', bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' },
                                { role: 'C', labelKey: 'consulted', descKey: 'consultedDesc', color: 'yellow', bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200' },
                                { role: 'I', labelKey: 'informed', descKey: 'informedDesc', color: 'red', bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200' },
                                { role: '-', labelKey: 'notInvolved', descKey: 'notInvolvedDesc', color: 'gray', bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200' },
                            ].map(item => (
                                <div key={item.role} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-blue-50/30 transition-all duration-200 border border-transparent hover:border-blue-100">
                                    <span className={`flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full ${item.bg} ${item.text} font-bold text-sm shadow-sm border ${item.border}`}>
                                        {item.role}
                                    </span>
                                    <div>
                                        <p className="text-sm font-medium text-slate-800">{t(`context.raci.${item.labelKey}`)}</p>
                                        <p className="text-xs text-slate-500">{t(`context.raci.${item.descKey}`)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Section 3: RACI Visualization */}
            <motion.div
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                transition={{ delay: 0.2 }} // Stagger animation slightly
            >
                {(displayParticipants.length > 0 && displayWorkshops.length > 0) ? (
                    <div className="bg-white p-6 rounded-xl shadow-lg border border-slate-100 overflow-hidden">


                        <RACIVisualization
                            key="live-viz"
                            participants={displayParticipants} // Already validated as array
                            workshops={displayWorkshops} // Already validated as array
                            matrix={displayMatrix} // Already validated as object

                        />

                    </div>
                ) : (
                    <div className="bg-white p-6 rounded-xl shadow-lg border border-slate-100 overflow-hidden">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-semibold text-slate-800 flex items-center">
                                <BarChart2 size={20} className="mr-2 text-blue-600" />
                                {t('context.sections.visualization')}
                            </h2>

                            <div className="hidden sm:block px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                                {t('context.steps.step3')}
                            </div>
                        </div>
                        <div className="text-center py-12 px-4 border border-dashed border-slate-300 rounded-lg bg-slate-50">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 text-slate-400 mb-4">
                                <BarChart2 size={28} />
                            </div>
                            <p className="text-slate-700 font-medium text-lg mb-2">{t('context.table.cannotDisplayVisualization')}</p>
                            <p className="text-sm text-slate-500 max-w-md mx-auto">{t('context.table.addParticipantsHint')}</p>
                        </div>
                    </div>
                )}
            </motion.div>

            {/* Section 4: Responsible Persons for Control Measures */}
            <motion.div
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                transition={{ delay: 0.3 }}
                className="space-y-6"
            >
                <div className="bg-white p-6 rounded-xl shadow-lg border border-slate-100">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
                        <h2 className="text-xl font-semibold text-slate-800 flex items-center">
                            <Briefcase size={20} className="mr-2 text-blue-600" />
                            {t('context.responsiblePersons.title')}
                        </h2>
                        <div className="flex gap-2">
                            <Button
                                variant="primary"
                                color="blue"
                                icon={<Save />}
                                onClick={handleSaveData}
                                disabled={isWorkshopDataLoading}
                                title={t('context.tooltips.save')}
                            >
                                {t('context.buttons.save')}
                            </Button>
                        </div>
                    </div>

                    {/* Add Responsible Person Inline Form */}
                    <div className="mb-4 p-4 bg-gradient-to-r from-slate-50 to-blue-50/30 rounded-lg border border-slate-200">
                        <div className="flex flex-wrap items-end gap-3">
                            <div className="flex-1 min-w-[200px]">
                                <label className="block text-xs font-medium text-slate-600 mb-1">
                                    {t('context.responsiblePersons.name')} <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={newResponsibleName}
                                    onChange={(e) => setNewResponsibleName(e.target.value)}
                                    onKeyPress={handleResponsibleKeyPress}
                                    placeholder={t('context.responsiblePersons.namePlaceholder')}
                                    className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                                />
                            </div>
                            <div className="flex-1 min-w-[200px]">
                                <label className="block text-xs font-medium text-slate-600 mb-1">
                                    {t('context.responsiblePersons.role')}
                                </label>
                                <input
                                    type="text"
                                    value={newResponsibleRole}
                                    onChange={(e) => setNewResponsibleRole(e.target.value)}
                                    onKeyPress={handleResponsibleKeyPress}
                                    placeholder={t('context.responsiblePersons.rolePlaceholder')}
                                    className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                                />
                            </div>
                            <div>
                                <Button
                                    variant="primary"
                                    color="blue"
                                    icon={<Plus />}
                                    onClick={handleAddResponsiblePerson}
                                    disabled={!newResponsibleName.trim()}
                                >
                                    {t('context.responsiblePersons.addButton')}
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Responsible Persons List */}
                    {responsiblePersonsList.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-slate-50 border-b border-slate-200">
                                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">{t('context.responsiblePersons.name')}</th>
                                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">{t('context.responsiblePersons.role')}</th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700 w-32">{t('common.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {responsiblePersonsList.map((responsible, index) => (
                                        <tr key={responsible.id} className={`border-b border-slate-100 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} hover:bg-blue-50/30 transition-colors`}>
                                            {editingResponsibleId === responsible.id ? (
                                                // Edit Mode
                                                <>
                                                    <td className="px-4 py-2">
                                                        <input
                                                            type="text"
                                                            value={editingResponsibleName}
                                                            onChange={(e) => setEditingResponsibleName(e.target.value)}
                                                            onKeyDown={handleEditKeyPress}
                                                            className="w-full p-2 border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                            autoFocus
                                                        />
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        <input
                                                            type="text"
                                                            value={editingResponsibleRole}
                                                            onChange={(e) => setEditingResponsibleRole(e.target.value)}
                                                            onKeyDown={handleEditKeyPress}
                                                            className="w-full p-2 border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-2 text-center">
                                                        <div className="flex items-center justify-center gap-1">
                                                            <button
                                                                onClick={handleSaveEditResponsible}
                                                                className="p-1.5 text-green-600 hover:bg-green-100 rounded-lg transition-colors duration-200"
                                                                title={t('common.save')}
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                                </svg>
                                                            </button>
                                                            <button
                                                                onClick={handleCancelEditResponsible}
                                                                className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors duration-200"
                                                                title={t('common.cancel')}
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </>
                                            ) : (
                                                // View Mode
                                                <>
                                                    <td className="px-4 py-3 text-sm text-slate-800 font-medium">{responsible.name}</td>
                                                    <td className="px-4 py-3 text-sm text-slate-600">{responsible.role || '-'}</td>
                                                    <td className="px-4 py-3 text-center">
                                                        <div className="flex items-center justify-center gap-1">
                                                            <button
                                                                onClick={() => handleStartEditResponsible(responsible)}
                                                                className="p-1.5 text-blue-500 hover:bg-blue-100 rounded-lg transition-colors duration-200"
                                                                title={t('common.edit')}
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                                                </svg>
                                                            </button>
                                                            <button
                                                                onClick={() => handleRemoveResponsiblePerson(responsible.id)}
                                                                className="p-1.5 text-red-500 hover:bg-red-100 rounded-lg transition-colors duration-200"
                                                                title={t('common.delete')}
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-8 px-4 border border-dashed border-slate-300 rounded-lg bg-slate-50">
                            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 text-slate-400 mb-3">
                                <Briefcase size={24} />
                            </div>
                            <p className="text-slate-600 font-medium">{t('context.responsiblePersons.noResponsibles')}</p>
                            <p className="text-sm text-slate-500 mt-1">{t('context.responsiblePersons.addResponsiblesHint')}</p>
                        </div>
                    )}

                    {/* Info about how this list is used */}
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                        <p className="text-sm text-blue-700 flex items-start">
                            <Info size={16} className="mr-2 flex-shrink-0 mt-0.5" />
                            <span>
                                <strong>{t('context.advice.title')}</strong> {t('context.responsiblePersons.infoHint')}
                            </span>
                        </p>
                    </div>
                </div>
            </motion.div>

            {/* === SECTION 5: Risk Levels Configuration (Read-only) === */}
            <motion.div variants={cardVariants} initial="hidden" animate="visible" className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100">
                    <div className="flex items-center">
                        <BarChart2 size={20} className="text-blue-600 mr-3" />
                        <h2 className="text-lg font-bold text-slate-800">{t('context.riskLevels.title', 'Configuration des niveaux de risque et niveaux de gravité')}</h2>
                    </div>
                    <p className="text-sm text-slate-500 mt-1 ml-9">{t('context.riskLevels.info', 'Configuration définie lors de la création de l\'analyse et modifiable dans l\'atelier 5.')}</p>
                </div>
                <div className="p-6 space-y-4">
                    <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-slate-700">{t('context.riskLevels.gravityLevels', 'Niveaux de gravité')} :</span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {gravityLevelsConfig.length} {t('context.riskLevels.levels', 'niveaux')}
                        </span>
                    </div>
                    <div className="overflow-x-auto rounded-lg border border-slate-200">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-200">
                                    <th className="text-left px-4 py-2.5 font-semibold text-slate-600 text-xs uppercase tracking-wider w-16">{t('context.riskLevels.levelCol', 'Niveau')}</th>
                                    <th className="text-left px-4 py-2.5 font-semibold text-slate-600 text-xs uppercase tracking-wider w-44">{t('context.riskLevels.labelCol', 'Libellé')}</th>
                                    <th className="text-left px-4 py-2.5 font-semibold text-slate-600 text-xs uppercase tracking-wider">{t('context.riskLevels.descriptionCol', 'Description')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {gravityLevelsConfig.map(g => (
                                    <tr key={g.code || g.level} className="hover:bg-slate-50/50">
                                        <td className="px-4 py-2.5 align-top">
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold bg-blue-100 text-blue-800">
                                                {g.code || `G${g.level}`}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2.5 text-sm font-semibold text-slate-800 align-top">{g.label}</td>
                                        <td className="px-4 py-2.5 text-xs text-slate-500 leading-relaxed">{g.description || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <RiskAcceptanceTable levels={riskLevelsConfig} />
                </div>
            </motion.div>

        </div>
    );
};

export default Context;
